/**
 * APP CONFIG SERVICE — Branding & platform configuration from Firestore
 * Loaded once from Firestore, cached on success only (#1993). Fallbacks to Befret defaults.
 */

import { Injectable, Optional } from '@angular/core';
import { Firestore, doc, getDoc, setDoc, DocumentSnapshot } from '@angular/fire/firestore';
import { Functions, httpsCallable } from '@angular/fire/functions';

/**
 * #1368 — L'URL DU LOGO DU BACKOFFICE, DÉCLARÉE ICI ET NULLE PART AILLEURS.
 *
 * Elle n'est PAS administrable : le logo est un asset de marque versionné, pas
 * un paramètre d'exploitation. Le champ « URL du logo » de Platform Config a
 * été retiré parce qu'il aurait fait une seconde source face à
 * `functions/src/config/branding.ts` (source unique des e-mails, #1356) — et
 * parce qu'un champ libre injecté dans le `src` d'une image est une surface
 * ouverte. Un document `app_config` qui porterait encore `branding.logoUrl`
 * n'est pas une erreur : la clé est simplement ignorée.
 */
export const BACKOFFICE_LOGO_URL = 'assets/befret_logo.png';

export interface AppBranding {
  companyName: string;
  appTitle: string;
  faviconUrl: string;
  primaryColor: string;
  accentColor: string;
  supportEmail: string;
  supportPhone: string;
  website: string;
  /**
   * #2186 — surcharges des liens réseaux sociaux des E-MAILS. Vide = le défaut
   * du code (`functions/src/config/social-links.ts`), qui est aussi celui du
   * pied de page public. Le défaut n'est jamais recopié ici : enregistrer
   * Platform Config ne doit pas figer l'URL du jour dans `app_config`.
   */
  facebookUrl: string;
  instagramUrl: string;
}

export type NotificationMode = 'production' | 'redirect' | 'disabled';

export interface RedirectEmployee {
  id: string;       // Employee Firestore doc ID
  name: string;     // Cached name for display
  email: string;    // Cached email for gateway
  phone: string;    // Cached phone for SMS gateway
}

export interface NotificationConfig {
  mode: NotificationMode;
  redirectEmployees: RedirectEmployee[];  // Employees receiving redirected notifications
  redirectEmails?: string[];              // DEPRECATED — kept for backward compat
  channels: {
    email: boolean;               // SendGrid
    sms: boolean;                 // Twilio SMS
    whatsapp: boolean;            // Twilio WhatsApp
    push: boolean;                // Push notifications
  };
  logAll: boolean;                // Log all notifications even when disabled
}

/** #812 — RBAC dynamique (#533 étape 1) : flag d'activation par environnement. */
export interface RbacConfig {
  /** true ⇒ PermissionService résout via la matrice ACL (rbac_acl). false = comportement legacy. */
  enabled: boolean;
}

/** #867 — durcissements de sécurité activables par environnement. */
export interface SecurityConfig {
  /**
   * true ⇒ les Cloud Functions privilégiées exigent une vérification récente du
   * second facteur (claim `mfaAt`, fenêtre de 12 h).
   *
   * ⚠️ À n'activer qu'une fois la claim en circulation ET le 2FA activé sur tous
   * les comptes privilégiés : sinon ils sont bloqués (un compte sans 2FA ne peut
   * pas obtenir la claim). Rollback = repasser à false, effet immédiat.
   */
  mfaRequiredForPrivilegedOps: boolean;
}

export interface AppConfig {
  branding: AppBranding;
  features: Record<string, boolean>;
  notifications: NotificationConfig;
  rbac: RbacConfig;
  security: SecurityConfig;
}

// ⚠️ #536b — types NotificationConfig/RedirectEmployee et défauts dupliqués
// avec functions/src/services/notification-gateway.ts (DEFAULT_CONFIG) —
// modifier les deux (aucun package partagé front/functions dans ce repo).
// ⚠️ #1225 — ce défaut est celui de l'ÉCRAN (ce qu'il propose tant que rien
// n'est enregistré), pas celui du serveur. Ils ne peuvent PAS être identiques :
// côté serveur, le repli dépend de l'environnement (`production` en production,
// `disabled` ailleurs), et le frontend backoffice ne connaît pas l'environnement
// des Cloud Functions.
//
// Ce que l'écran ne doit surtout pas faire, c'est laisser croire que « production »
// est ce qui s'applique tant qu'on n'a rien enregistré : hors production, ce qui
// s'applique est `disabled`. C'est le composant qui le dit à l'écran — cf. la
// note de périmètre de `platform-config.component.html`.
// ⚠️ #1357 — `whatsapp` par défaut à FALSE. Le défaut historique (`true`)
// affirmait qu'un canal WhatsApp était actif alors qu'aucune passerelle ne lit
// ce drapeau et qu'aucun envoi WhatsApp ne part sur les chemins de notification
// client (`sendSmsWithRetry` construit toujours un SMS classique). Un défaut
// activant un canal qui n'envoie rien se propage aux écrans qui l'affichent —
// c'est ce qui faisait annoncer « WhatsApp + email » à l'expédition d'un
// groupage. Le jour où WhatsApp envoie vraiment, c'est un opt-in explicite.
const DEFAULT_NOTIFICATIONS: NotificationConfig = {
  mode: 'production',
  redirectEmployees: [],
  channels: { email: true, sms: true, whatsapp: false, push: false },
  logAll: true,
};

// #812 — défaut false : l'activation est un opt-in par environnement (doc
// app_config/main) ; repasser enabled à false = rollback instantané sans deploy.
const DEFAULT_RBAC: RbacConfig = { enabled: false };

// #867 — défaut false, comme le flag RBAC : le déploiement ne change rien tant
// que quelqu'un ne l'active pas sciemment.
const DEFAULT_SECURITY: SecurityConfig = { mfaRequiredForPrivilegedOps: false };

const DEFAULT_CONFIG: AppConfig = {
  branding: {
    companyName: 'BeFret',
    appTitle: 'Backoffice',
    faviconUrl: '',
    primaryColor: '#22A922',
    accentColor: '#1565C0',
    supportEmail: 'support@befret.be',
    supportPhone: '+32 2 123 45 67',
    website: 'https://befret.be',
    facebookUrl: '',
    instagramUrl: '',
  },
  features: {
    logistics_europe: true,
    logistics_africa: true,
    virtual_mailbox: true,
    customer_service: true,
    crm: true,
    hr: true,
    finance: true,
    it: true,
    marketing: true,
    knowledge: true,
    monitoring: true,
  },
  notifications: DEFAULT_NOTIFICATIONS,
  rbac: DEFAULT_RBAC,
  security: DEFAULT_SECURITY,
};

/**
 * Copie fraîche des défauts — jamais d'alias sur DEFAULT_CONFIG : les setters
 * (saveBranding/saveFeatures/saveRbac) réassignent des sous-objets de _config,
 * et un alias muterait l'objet module-level partagé entre instances (fuite
 * inter-instances détectée par les specs #812, ordre Jasmine aléatoire).
 */
function freshDefaultConfig(): AppConfig {
  return {
    branding: { ...DEFAULT_CONFIG.branding },
    features: { ...DEFAULT_CONFIG.features },
    notifications: {
      ...DEFAULT_NOTIFICATIONS,
      redirectEmployees: [],
      channels: { ...DEFAULT_NOTIFICATIONS.channels },
    },
    rbac: { ...DEFAULT_RBAC },
    security: { ...DEFAULT_SECURITY },
  };
}

/** #1993 — message montré quand un enregistrement est refusé (configuration non lue). */
export const CONFIG_NOT_LOADED_MESSAGE =
  'Configuration non chargée depuis le serveur : enregistrement refusé pour ne pas écraser la configuration réelle. '
  + 'Rechargez la page après la vérification en deux étapes, puis réessayez.';

@Injectable({ providedIn: 'root' })
export class AppConfigService {

  private _config: AppConfig = freshDefaultConfig();
  /**
   * #1993 — vrai SEULEMENT après une lecture réussie de `app_config/main`.
   * Un échec (déconnecté, ou 2FA pas encore passée : la règle exige
   * `isEmployee()`) n'est jamais mis en cache : la lecture suivante réessaie.
   * Avant, l'échec posait le drapeau et les défauts servaient toute la vie de
   * la page — et les écrans d'administration les réécrivaient à l'enregistrement.
   */
  private _loadedFromServer = false;
  /** Lecture en cours, partagée par les appels simultanés. */
  private _loading: Promise<void> | null = null;

  // `Functions` est optionnel pour les specs qui construisent le service à la
  // main ; l'application l'injecte toujours (provideFunctions, app.config).
  constructor(private firestore: Firestore, @Optional() private functions?: Functions) {}

  /**
   * #1900 — `security` et `rbac` ne s'écrivent plus depuis le navigateur (les
   * règles le refusent à tout rôle) : la callable `updatePlatformSecurity`
   * contrôle le palier admin et le second facteur, puis journalise avant/après
   * dans `rbac_audit`. Elle renvoie l'état écrit, repris tel quel ici.
   */
  private async updatePlatformSecurity(patch: Record<string, unknown>): Promise<{
    security: { mfaRequiredForPrivilegedOps: boolean; breakGlassAccounts: string[] };
    rbac: { enabled: boolean };
  }> {
    if (!this.functions) throw new Error('Cloud Functions indisponibles');
    const call = httpsCallable<Record<string, unknown>, any>(this.functions, 'updatePlatformSecurity');
    return (await call(patch)).data;
  }

  /** La configuration affichée vient-elle du serveur (et non des défauts) ? */
  get loadedFromServer(): boolean { return this._loadedFromServer; }

  /** Lecture de `app_config/main` — couture isolée pour les tests. */
  private readConfigDoc(): Promise<DocumentSnapshot> {
    return getDoc(doc(this.firestore, 'app_config', 'main'));
  }

  /**
   * Charge la configuration depuis Firestore. Sans effet une fois chargée ;
   * réessaie après un échec.
   */
  async init(): Promise<void> {
    if (this._loadedFromServer) return;
    if (!this._loading) {
      this._loading = this.load().finally(() => { this._loading = null; });
    }
    return this._loading;
  }

  private async load(): Promise<void> {
    try {
      const snap = await this.readConfigDoc();
      if (snap.exists()) {
        const data = snap.data();
        const notifData = data['notifications'] || {};
        this._config = {
          branding: { ...DEFAULT_CONFIG.branding, ...(data['branding'] || {}) },
          features: { ...DEFAULT_CONFIG.features, ...(data['features'] || {}) },
          notifications: {
            ...DEFAULT_NOTIFICATIONS,
            ...notifData,
            redirectEmployees: Array.isArray(notifData.redirectEmployees) ? notifData.redirectEmployees : [],
            channels: { ...DEFAULT_NOTIFICATIONS.channels, ...(notifData.channels || {}) },
          },
          rbac: { ...DEFAULT_RBAC, ...(data['rbac'] || {}) },
          security: { ...DEFAULT_SECURITY, ...(data['security'] || {}) },
        };
      }
      // Document absent : les défauts SONT la configuration du serveur.
      this._loadedFromServer = true;
    } catch (err) {
      console.warn('[AppConfig] Lecture impossible, défauts affichés sans être mis en cache — nouvel essai à la prochaine lecture :', err);
    }
  }

  /**
   * #1993 — refuse tout enregistrement tant que la configuration n'a pas été
   * lue depuis le serveur : on réécrirait les défauts par-dessus la vraie
   * configuration (notifications sans redirection, RBAC, modules).
   */
  private assertLoadedFromServer(): void {
    if (!this._loadedFromServer) {
      throw new Error(CONFIG_NOT_LOADED_MESSAGE);
    }
  }

  get config(): AppConfig { return this._config; }
  get branding(): AppBranding { return this._config.branding; }

  isFeatureEnabled(feature: string): boolean {
    return this._config.features[feature] !== false;
  }

  /** Save branding config (admin only) */
  async saveBranding(branding: Partial<AppBranding>): Promise<void> {
    this.assertLoadedFromServer();
    const ref = doc(this.firestore, 'app_config', 'main');
    const merged = { ...this._config.branding, ...branding };
    await setDoc(ref, { branding: merged, features: this._config.features }, { merge: true });
    this._config.branding = merged;
  }

  /** Save feature toggles (admin only) */
  async saveFeatures(features: Record<string, boolean>): Promise<void> {
    this.assertLoadedFromServer();
    const ref = doc(this.firestore, 'app_config', 'main');
    const merged = { ...this._config.features, ...features };
    await setDoc(ref, { branding: this._config.branding, features: merged }, { merge: true });
    this._config.features = merged;
  }

  get notifications(): NotificationConfig { return this._config.notifications; }

  get rbac(): RbacConfig { return this._config.rbac; }

  /** #812 — flag RBAC dynamique. Strictement === true : toute valeur absente/malformée ⇒ legacy. */
  get rbacEnabled(): boolean {
    return this._config.rbac.enabled === true;
  }

  /** Save RBAC config (admin only — #1900 : par la callable tracée) */
  async saveRbac(rbac: RbacConfig): Promise<void> {
    this.assertLoadedFromServer();
    const saved = await this.updatePlatformSecurity({ rbacEnabled: rbac.enabled === true });
    this._config.rbac = { ...this._config.rbac, ...rbac, enabled: saved.rbac.enabled };
  }

  /** #867 — strictement === true : toute valeur absente/malformée ⇒ non exigé. */
  get mfaRequiredForPrivilegedOps(): boolean {
    return this._config.security.mfaRequiredForPrivilegedOps === true;
  }

  /** Save security config (admin only — #1900 : par la callable tracée) */
  async saveSecurity(security: SecurityConfig): Promise<void> {
    this.assertLoadedFromServer();
    const saved = await this.updatePlatformSecurity({
      mfaRequiredForPrivilegedOps: security.mfaRequiredForPrivilegedOps === true,
    });
    this._config.security = {
      ...this._config.security,
      ...security,
      mfaRequiredForPrivilegedOps: saved.security.mfaRequiredForPrivilegedOps,
    };
  }

  /** Save notification config (admin only) */
  async saveNotifications(notif: NotificationConfig): Promise<void> {
    this.assertLoadedFromServer();
    const ref = doc(this.firestore, 'app_config', 'main');
    await setDoc(ref, { notifications: notif }, { merge: true });
    this._config.notifications = { ...notif };
  }
}
